/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { map, some, get, flatten, isNumber } from 'lodash'

import SidebarModal from './SidebarModal'
import ModifierGroup from './ModifierGroup'

import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import quantityValidator from '../utils/quantityValidator'
import quantityUpdater from '../utils/quantityUpdater'
import { NOTES_CHARACTER_LIMIT } from '../constants'

import styles from './CartItemDetailsModal.module.scss'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import { Button, ButtonBase } from '@material-ui/core'
import VNItemDetailOpenPrice from '../VNItems/components/VNItemDetailOpenPrice'
import { floatToCents } from '../VNUtil/VNUtils'
import { setManagerApprovalPrompt } from '../VNManagerApproval/ActionCreators'
import VNPriceIndicator from '../VNItems/components/VNPriceIndicator'
import { getManagerId } from '../selectors/attendant'
import { getLatestPriceAdjustment } from '../VNDialogs/Selectors'
import { setPriceAdjustDialog, setPriceAdjustExitConfirmation } from '../VNDialogs/ActionCreators'
import { isE700Device } from '../VNAndroidSDK/Utils'

const textFieldStyles = makeStyles({
  root: {
    // focused color for input with variant='outlined'
    '& .MuiOutlinedInput-root': {
      '&.MuiInputBase-formControl fieldset': {
        border: 0,
      },
      '&.Mui-focused fieldset': {
        border: 0,
      },
    },
  },
})

const CartItemDetailsModal = ({
  cartItemNotValidIfClosed,
  dispatch,
  orderMaxAmount,
  orderMinCount,
  id,
  customTitle,
  title,
  price,
  customPriceInCents,
  defaultPriceInCents,
  quantity,
  canEnterNotes,
  notes,
  notesAreEmpty,
  modifierGroups,
  selectedItemIds,
  onClose,
  onRemove,
  className,
  addToCart,
  updateModifiers,
  updateOpenPriceItem,
  updateNotes,
  updateCartItemQuantity,
  isKeyboardActive,
  isOpenPriceItem,
  ...props
}) => {
  const classNames = cn(styles.cartItemDetailsModal, className)
  const classes = textFieldStyles()
  const isMobile = useGetIsMobileScreen()
  const [modifierGroupsSelections, setModifierGroupsSelections] = useState(selectedItemIds)
  const [validators, setValidators] = useState(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  const [isValid, setIsValid] = useState(false)
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const [itemNotes, setItemNotes] = useState(notes)
  const [inputValue, setInputValue] = useState(quantity)
  const [isQuantityUpdating, setIsQuantityUpdating] = useState(false)
  const [inputPlaceholder, setInputPlaceholder] = useState(quantity)
  const [openPrice, setOpenPrice] = useState(price)
  const [openName, setOpenName] = useState(customTitle)
  const managerId = useSelector(getManagerId)
  const priceAdjustmentInCents = useSelector(getLatestPriceAdjustment)
  const hasPriceAdjustment = useMemo(() => {
    if (priceAdjustmentInCents === null) {
      return false
    }

    //need to look for new price vs item price or new price vs old price
    return priceAdjustmentInCents !== defaultPriceInCents || priceAdjustmentInCents !== customPriceInCents
  }, [priceAdjustmentInCents, defaultPriceInCents, customPriceInCents])
  const clientHeight = document?.body?.clientHeight

  const onSelect = (itemId, index) => {
    const modifierGroup = modifierGroups[index]
    const { min, max } = modifierGroup
    const selectedModifiers = modifierGroupsSelections[index]

    const nextSelectedModifiers = [...modifierGroupsSelections]
    nextSelectedModifiers[index] = quantityUpdater(min, max, selectedModifiers, itemId)

    setModifierGroupsSelections(nextSelectedModifiers)
  }

  useEffect(() => {
    setInputPlaceholder(quantity)
  }, [quantity])

  useEffect(() => {
    const validations = map(validators, (validator, index) => {
      const modifierGroupSelections = modifierGroupsSelections[index]

      return validator.isValid(get(modifierGroupSelections, 'length', 0))
    })

    const isValid = !some(validations, (valid) => valid === false)
    setIsValid(isValid)
  }, modifierGroupsSelections)

  useEffect(() => {
    setValidators(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
    setModifierGroupsSelections(selectedItemIds)
  }, [modifierGroups])

  let notesElement = null

  const hasCharacterMax = isNumber(NOTES_CHARACTER_LIMIT)

  const handleSetNotes = (event) => {
    const notes = get(event, 'target.value', '')
    let trimmedNotes = notes

    if (hasCharacterMax) {
      trimmedNotes = notes?.substring(0, NOTES_CHARACTER_LIMIT) || ''
    }
    setItemNotes(trimmedNotes)
  }

  const onFocus = () => {
    if (!isQuantityUpdating) {
      setIsKeyboardOpen(true)
    }
  }
  const onBlur = () => {
    setIsKeyboardOpen(false)
  }

  if (canEnterNotes) {
    notesElement = (
      <div className={cn(styles.notesContainer, { [styles.emptyNotes]: notesAreEmpty })}>
        <TextField
          id="special-instructions"
          label="Add Special Instructions"
          value={itemNotes}
          onChange={handleSetNotes}
          className={cn(styles.notes, classes.root)}
          InputLabelProps={{ className: styles.textfield__label }}
          inputProps={{ 'data-allow-keyboard-activity': true }}
          InputProps={{ className: styles.textfield__input }}
          variant="outlined"
          multiline
          onFocus={onFocus}
          onBlur={onBlur}
          rows={5}
        />
      </div>
    )
  }

  const incrementQuantity = () => {
    const newValue = inputValue === orderMaxAmount ? orderMaxAmount : inputValue + 1
    setInputValue(newValue)
  }

  const decrementQuantity = () => {
    const newValue = inputValue === orderMinCount ? orderMinCount : inputValue - 1
    setInputValue(newValue)
  }

  const handleInput = (e) => {
    const input = get(e, 'target.value', '')
    setInputValue(input)
  }

  const handleSetQuantity = (inputValue) => {
    let newValue = inputValue
    if (!inputValue || inputValue < orderMinCount) {
      newValue = orderMinCount
    }
    if (inputValue > orderMaxAmount) {
      newValue = orderMaxAmount
    }
    setInputValue(newValue)
  }

  const handleOpenPriceItemUpdate = (approverId = null) => {
    updateOpenPriceItem(floatToCents(openPrice), null, openName, approverId)
    onClose(true)
  }

  const handleUpdateClick = (e) => {
    updateCartItemQuantity(inputValue)
    updateModifiers(flatten(modifierGroupsSelections))

    if (notes !== itemNotes) {
      updateNotes(itemNotes)
    }

    // Price Adjustment is updated on "Update" from this drawer
    // whereas "OpenPrice" is updated from the "Manager Approval" screen
    if (hasPriceAdjustment) {
      updateOpenPriceItem(priceAdjustmentInCents, defaultPriceInCents, null, managerId)
      dispatch(setPriceAdjustDialog(false))
    }

    if (!isOpenPriceItem) {
      onClose(true)
      return
    }

    // a name change only
    // this makes it so we don't have to get manager approval for that
    if (price === openPrice) {
      handleOpenPriceItemUpdate()
      return
    }

    // callback has managerId as a parameter
    dispatch(setManagerApprovalPrompt({ onSuccess: handleOpenPriceItemUpdate, buttonText: 'Update Item' }))
  }

  const handleClose = () => {
    if (hasPriceAdjustment) {
      dispatch(setPriceAdjustExitConfirmation())
      return
    }

    onClose(cartItemNotValidIfClosed ? false : isValid)
  }

  const displayPriceItem = () => {
    if (isOpenPriceItem) {
      return (
        <div className={cn(styles.header, styles.noBorder)}>
          <div className={styles.itemName}>{title}</div>
          <VNItemDetailOpenPrice
            openPrice={openPrice}
            handleSetOpenPrice={setOpenPrice}
            openName={openName}
            handleSetOpenName={setOpenName}
            itemName={customTitle}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      )
    }

    return (
      <div className={styles.header}>
        <div className={styles.itemName}>{title}</div>
        <VNPriceIndicator
          priceAdjustmentInCents={priceAdjustmentInCents ?? customPriceInCents}
          defaultPriceInCents={defaultPriceInCents}
        />
      </div>
    )
  }

  return (
    <SidebarModal show title="Item Detail" onClose={handleClose} className={cn(classNames, styles.header)} {...props}>
      <div className={styles.cartItemDetailsModal}>
        <div className={styles.scrollable} style={isKeyboardOpen ? { height: '80vh' } : null}>
          {displayPriceItem()}
          {modifierGroups.map((modifierGroup, index) => {
            if (!modifierGroup.name) {
              return null
            }
            return (
              <ModifierGroup
                key={index}
                className={styles.modifierGroup}
                title={modifierGroup.name}
                label={get(validators, `[${index}].label`)}
                items={modifierGroup.items}
                selectedItemIds={modifierGroupsSelections[index]}
                isRequired={get(validators, `[${index}].validationType`) === quantityValidator.types.required}
                min={modifierGroup.min}
                max={modifierGroup.max}
                onSelect={(itemId) => onSelect(itemId, index)}
                uuid={modifierGroup.uuid}
              />
            )
          })}
          {notesElement}
        </div>
        {(!isKeyboardActive || isQuantityUpdating) && (
          <div className={styles.footer} style={isMobile && isQuantityUpdating && clientHeight > 800 ? { top: '48vh' } : null}>
            <div className={styles.updateQuantity}>
              <ButtonBase
                disableRipple={isE700Device()}
                component="span"
                className={
                  inputValue <= orderMinCount ? cn(styles.disabled, styles.minus) : cn(styles.quantityOperator, styles.minus)
                }
                onClick={decrementQuantity}
              >
                –
              </ButtonBase>
              <input
                type="number"
                className={styles.quantity}
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={handleInput}
                onFocus={() => {
                  setIsQuantityUpdating(true)
                  setInputPlaceholder('')
                }}
                onBlur={() => {
                  setIsQuantityUpdating(false)
                  handleSetQuantity(+inputValue)
                }}
                pattern="\d*"
              />
              <ButtonBase
                disableRipple={isE700Device()}
                component="span"
                className={
                  inputValue >= orderMaxAmount ? cn(styles.disabled, styles.plus) : cn(styles.quantityOperator, styles.plus)
                }
                onClick={incrementQuantity}
              >
                +
              </ButtonBase>
            </div>
            <div className={styles.update}>
              <Button className={styles.doneButton} disabled={!isValid} onClick={handleUpdateClick}>
                <span>Update</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </SidebarModal>
  )
}

export default CartItemDetailsModal
